import styled from "styled-components";
import { useNavigate, useLocation } from "react-router-dom";

import { Image } from "components/atoms";

import { Property } from "models/properties";

import { BackgroundImageSize } from "interfaces/images";

import { MAIN_THEME } from "styles/colors";
import { FONT_FAMILY_MEDIUM } from "styles/typography";

interface Props {
	data: Property;
}

const Container = styled.div`
	background: transparent;
	border: none;
	border-radius: 0;
	display: grid;
	grid-template-columns: 100px 180px 1fr;
	gap: 15px;
	align-items: center;
	padding: 10px 15px;
	transition: background 0.15s ease;

	&:hover {
		background: ${MAIN_THEME.TABLE_HOVER};
		cursor: pointer;
	}

	@media (max-width: 1200px) {
		padding: 10px 15px;
		gap: 12px;
	}

	@media (max-width: 768px) {
		grid-template-columns: 60px 1fr;
		grid-template-areas:
			"image title"
			"image address";
		gap: 2px 12px;
		padding: 12px;
		border: 1px solid ${MAIN_THEME.BORDER_COLOR};
		border-radius: 8px;
		margin-bottom: 8px;
		background: transparent;
		align-items: center;
	}
`;

const ImageCell = styled.div`
	display: flex;
	align-items: center;
	width: 100%;
	height: 80px;

	@media (max-width: 768px) {
		grid-area: image;
		height: 60px;
		width: 60px;
	}
`;

const TitleCell = styled.div`
	@media (max-width: 768px) {
		grid-area: title;
		align-self: end;
	}
`;

const Title = styled.p`
	text-transform: capitalize;
	color: ${MAIN_THEME.TEXT_PRIMARY};
	font-size: 16px;
	font-weight: 500;
	font-family: ${FONT_FAMILY_MEDIUM};
	margin: 0;

	@media (max-width: 768px) {
		font-size: 15px;
		font-weight: 600;
		line-height: 1.2;
	}

	@media (max-width: 480px) {
		font-size: 14px;
	}
`;

const Address = styled.p`
	color: ${MAIN_THEME.TEXT_SECONDARY};
	font-size: 15px;
	margin: 0;
	font-weight: 400;
	line-height: 1.4;

	@media (max-width: 768px) {
		grid-area: address;
		font-size: 12px;
		line-height: 1.3;
		align-self: start;
	}

	@media (max-width: 480px) {
		font-size: 12px;
	}
`;

const PropertyCard = (props: Props): JSX.Element => {
	const { data } = props;

	const { id, name, picture, address } = data;

	const navigate = useNavigate();
	const location = useLocation();

	const handleNavigateToDetails = (): void => {
		navigate(`${location.pathname}/${id}`);
	};

	const renderTitle = () => {
		const nameWithoutDash = name.replaceAll("-", " ");
		const nameWithoutUnderscore = nameWithoutDash.replaceAll("_", " ");

		return <Title>{nameWithoutUnderscore}</Title>;
	};

	return (
		<Container onClick={handleNavigateToDetails}>
			<ImageCell>
					<Image
						src={picture}
						isBackground
						fullWidth
						fullHeight
						borderRadius={8}
						backgroundSize={BackgroundImageSize.COVER}
					/>

			</ImageCell>
			<TitleCell>{renderTitle()}</TitleCell>
			<Address>{address.display}</Address>
		</Container>
	);
};

export default PropertyCard;
